import {TRANSACTION_PRICES_SCOPE_LABEL} from "../constants/transaction_prices";
import {formatPercent, formatSignedPercent} from "./format_price_m2";
import {formatRcnPeriodLabel} from "./format_rcn_period_label";
import {ITransactionPriceRow} from "./get_transaction_price_rows";

export interface ITransactionPricesSummary {
    citiesCount: number;
    latestPeriodStart: string;
    latestPeriodLabel: string;
    earliestPeriodStart: string;
    earliestPeriodLabel: string;
    /** The period most cities share - the one worth naming in a column header. */
    dominantPeriodLabel: string;
    averageDifferencePct: number;
    widestGap: ITransactionPriceRow;
    narrowestGap: ITransactionPriceRow;
    /** The cities worth naming as the widest gaps - two of them, or one when the set is too small to keep both ends apart. */
    widestGaps: ITransactionPriceRow[];
    /** Counterpart of widestGaps, ordered from the narrowest gap up. */
    narrowestGaps: ITransactionPriceRow[];
}

/** Two cities per end need at least four of them, otherwise both ends would name the same city. */
const getGapCitiesCount = (citiesCount: number): number => (citiesCount >= 4 ? 2 : 1);

export const getTransactionPricesSummary = (rows: ITransactionPriceRow[]): ITransactionPricesSummary | null => {
    if (rows.length === 0) {
        return null;
    }

    const latestRow = rows.reduce((latest, row) => (row.periodStart > latest.periodStart ? row : latest), rows[0]);
    const earliestRow = rows.reduce((earliest, row) => (row.periodStart < earliest.periodStart ? row : earliest), rows[0]);
    const averageDifferencePct = Math.round((rows.reduce((sum, row) => sum + row.differencePct, 0) / rows.length) * 10) / 10;
    const sortedByGap = [...rows].sort((prev, next) => Math.abs(next.differencePct) - Math.abs(prev.differencePct));
    const gapCitiesCount = getGapCitiesCount(rows.length);

    const rowsByPeriod = new Map<string, ITransactionPriceRow[]>();

    for (const row of rows) {
        rowsByPeriod.set(row.periodLabel, [...(rowsByPeriod.get(row.periodLabel) ?? []), row]);
    }

    // most cities win; a tie goes to the newer period
    const dominantPeriodLabel = Array.from(rowsByPeriod.values()).reduce((dominant, candidate) =>
        candidate.length > dominant.length || (candidate.length === dominant.length && candidate[0].periodStart > dominant[0].periodStart)
            ? candidate
            : dominant
    )[0].periodLabel;

    return {
        citiesCount: rows.length,
        latestPeriodStart: latestRow.periodStart,
        latestPeriodLabel: latestRow.periodLabel,
        earliestPeriodStart: earliestRow.periodStart,
        earliestPeriodLabel: earliestRow.periodLabel,
        dominantPeriodLabel,
        averageDifferencePct,
        widestGap: sortedByGap[0],
        narrowestGap: sortedByGap[sortedByGap.length - 1],
        widestGaps: sortedByGap.slice(0, gapCitiesCount),
        narrowestGaps: rows.length < 2 ? [] : [...sortedByGap].reverse().slice(0, gapCitiesCount)
    };
};

export const isSingleTransactionPricesPeriod = (summary: ITransactionPricesSummary): boolean => summary.earliestPeriodLabel === summary.latestPeriodLabel;

export const getTransactionPricesPeriodLabel = (summary: ITransactionPricesSummary): string => {
    return isSingleTransactionPricesPeriod(summary)
        ? formatRcnPeriodLabel(summary.latestPeriodLabel)
        : `${formatRcnPeriodLabel(summary.earliestPeriodLabel)} – ${formatRcnPeriodLabel(summary.latestPeriodLabel)}`;
};

const getScopeLabel = (citiesCount: number): string => (citiesCount === 1 ? "analizowanym mieście" : `${citiesCount} analizowanych miastach`);

const getAverageDifferenceSentence = (summary: ITransactionPricesSummary): string => {
    const {citiesCount, averageDifferencePct} = summary;
    const scope = getScopeLabel(citiesCount);

    if (averageDifferencePct === 0) {
        return `W ${scope} kupujący płacili za ${TRANSACTION_PRICES_SCOPE_LABEL} średnio tyle samo, ile wynosiła cena z ogłoszenia.`;
    }

    const direction = averageDifferencePct < 0 ? "mniej" : "więcej";

    return `W ${scope} kupujący płacili za ${TRANSACTION_PRICES_SCOPE_LABEL} średnio o ${formatPercent(Math.abs(averageDifferencePct))} ${direction}, niż wynosiła cena z ogłoszenia.`;
};

const getPeriodSentence = (summary: ITransactionPricesSummary): string => {
    return isSingleTransactionPricesPeriod(summary)
        ? `Ceny z aktów notarialnych dotyczą okresu ${getTransactionPricesPeriodLabel(summary)}.`
        : `Ceny z aktów notarialnych dotyczą ostatniego dostępnego kwartału dla każdego miasta (${getTransactionPricesPeriodLabel(summary)}).`;
};

export const getTransactionPricesCommentary = (summary: ITransactionPricesSummary): string => {
    const {citiesCount, averageDifferencePct, widestGap, narrowestGap} = summary;
    const gapSentence =
        citiesCount > 1
            ? ` Największa różnica dotyczy miasta ${widestGap.name} (${formatSignedPercent(widestGap.differencePct)}), najmniejsza – miasta ${narrowestGap.name} (${formatSignedPercent(narrowestGap.differencePct)}).`
            : "";
    const caveat =
        averageDifferencePct < 0
            ? " Ta różnica to realna przestrzeń do negocjacji przy zakupie."
            : " Różnica wynika m.in. z innego okresu i struktury sprzedanych mieszkań.";

    return `${getAverageDifferenceSentence(summary)}${gapSentence}${caveat} ${getPeriodSentence(summary)}`;
};
import {TRANSACTION_PRICES_SCOPE_LABEL} from "../constants/transaction_prices";
import {formatPercent, formatSignedPercent} from "./format_price_m2";
import {formatRcnPeriodLabel} from "./format_rcn_period_label";
import {ITransactionPriceRow} from "./get_transaction_price_rows";

export interface ITransactionPricesSummary {
    citiesCount: number;
    latestPeriodStart: string;
    latestPeriodLabel: string;
    earliestPeriodStart: string;
    earliestPeriodLabel: string;
    /** The period most cities share - the one worth naming in a column header. */
    dominantPeriodLabel: string;
    averageDifferencePct: number;
    widestGap: ITransactionPriceRow;
    narrowestGap: ITransactionPriceRow;
    /** The cities worth naming as the widest gaps - two of them, or one when the set is too small to keep both ends apart. */
    widestGaps: ITransactionPriceRow[];
    /** Counterpart of widestGaps, ordered from the narrowest gap up. */
    narrowestGaps: ITransactionPriceRow[];
}

/** Two cities per end need at least four of them, otherwise both ends would name the same city. */
const getGapCitiesCount = (citiesCount: number): number => (citiesCount >= 4 ? 2 : 1);

export const getTransactionPricesSummary = (rows: ITransactionPriceRow[]): ITransactionPricesSummary | null => {
    if (rows.length === 0) {
        return null;
    }

    const latestRow = rows.reduce((latest, row) => (row.periodStart > latest.periodStart ? row : latest), rows[0]);
    const earliestRow = rows.reduce((earliest, row) => (row.periodStart < earliest.periodStart ? row : earliest), rows[0]);
    const averageDifferencePct = Math.round((rows.reduce((sum, row) => sum + row.differencePct, 0) / rows.length) * 10) / 10;
    const sortedByGap = [...rows].sort((prev, next) => Math.abs(next.differencePct) - Math.abs(prev.differencePct));
    const gapCitiesCount = getGapCitiesCount(rows.length);

    const rowsByPeriod = new Map<string, ITransactionPriceRow[]>();

    for (const row of rows) {
        rowsByPeriod.set(row.periodLabel, [...(rowsByPeriod.get(row.periodLabel) ?? []), row]);
    }

    // most cities win; a tie goes to the newer period
    const dominantPeriodLabel = Array.from(rowsByPeriod.values()).reduce((dominant, candidate) =>
        candidate.length > dominant.length || (candidate.length === dominant.length && candidate[0].periodStart > dominant[0].periodStart)
            ? candidate
            : dominant
    )[0].periodLabel;

    return {
        citiesCount: rows.length,
        latestPeriodStart: latestRow.periodStart,
        latestPeriodLabel: latestRow.periodLabel,
        earliestPeriodStart: earliestRow.periodStart,
        earliestPeriodLabel: earliestRow.periodLabel,
        dominantPeriodLabel,
        averageDifferencePct,
        widestGap: sortedByGap[0],
        narrowestGap: sortedByGap[sortedByGap.length - 1],
        widestGaps: sortedByGap.slice(0, gapCitiesCount),
        narrowestGaps: rows.length < 2 ? [] : [...sortedByGap].reverse().slice(0, gapCitiesCount)
    };
};

export const isSingleTransactionPricesPeriod = (summary: ITransactionPricesSummary): boolean => summary.earliestPeriodLabel === summary.latestPeriodLabel;

export const getTransactionPricesPeriodLabel = (summary: ITransactionPricesSummary): string => {
    return isSingleTransactionPricesPeriod(summary)
        ? formatRcnPeriodLabel(summary.latestPeriodLabel)
        : `${formatRcnPeriodLabel(summary.earliestPeriodLabel)} – ${formatRcnPeriodLabel(summary.latestPeriodLabel)}`;
};

const getScopeLabel = (citiesCount: number): string => (citiesCount === 1 ? "analizowanym mieście" : `${citiesCount} analizowanych miastach`);

const getAverageDifferenceSentence = (summary: ITransactionPricesSummary): string => {
    const {citiesCount, averageDifferencePct} = summary;
    const scope = getScopeLabel(citiesCount);

    if (averageDifferencePct === 0) {
        return `W ${scope} kupujący płacili za ${TRANSACTION_PRICES_SCOPE_LABEL} średnio tyle samo, ile wynosiła cena z ogłoszenia.`;
    }

    const direction = averageDifferencePct < 0 ? "mniej" : "więcej";

    return `W ${scope} kupujący płacili za ${TRANSACTION_PRICES_SCOPE_LABEL} średnio o ${formatPercent(Math.abs(averageDifferencePct))} ${direction}, niż wynosiła cena z ogłoszenia.`;
};

const getPeriodSentence = (summary: ITransactionPricesSummary): string => {
    return isSingleTransactionPricesPeriod(summary)
        ? `Ceny z aktów notarialnych dotyczą okresu ${getTransactionPricesPeriodLabel(summary)}.`
        : `Ceny z aktów notarialnych dotyczą ostatniego dostępnego kwartału dla każdego miasta (${getTransactionPricesPeriodLabel(summary)}).`;
};

export const getTransactionPricesCommentary = (summary: ITransactionPricesSummary): string => {
    const {citiesCount, averageDifferencePct, widestGap, narrowestGap} = summary;
    const gapSentence =
        citiesCount > 1
            ? ` Największa różnica dotyczy miasta ${widestGap.name} (${formatSignedPercent(widestGap.differencePct)}), najmniejsza – miasta ${narrowestGap.name} (${formatSignedPercent(narrowestGap.differencePct)}).`
            : "";
    const caveat =
        averageDifferencePct < 0
            ? " Ta różnica to realna przestrzeń do negocjacji przy zakupie."
            : " Różnica wynika m.in. z innego okresu i struktury sprzedanych mieszkań.";

    return `${getAverageDifferenceSentence(summary)}${gapSentence}${caveat} ${getPeriodSentence(summary)}`;
};
